// Grant-time SoD conflict pairs [02 §8.4]. A user may hold both sides only with an active
// `sod_exception` (reason, approver, expiry ≤ 12 months). Action-time SoD lives in ./check.ts.
import { PERMISSIONS } from './permissions.ts';
import type { PermissionKey } from './permissions.ts';

export interface SodRule {
  readonly id: string;
  readonly left: readonly PermissionKey[];
  readonly right: readonly PermissionKey[];
}

export const SOD_EXCEPTION_MAX_MONTHS = 12;

/** SOD-09 right side: every `*.approve`, `*.approve_on_behalf`, `*.post`, `*.post_*` and `ap.release_payment`. */
const EMPOWERING: readonly PermissionKey[] = PERMISSIONS.filter(
  (p) =>
    p.verb === 'approve' ||
    p.verb === 'approve_on_behalf' ||
    p.verb === 'post' ||
    p.verb.startsWith('post_') ||
    p.key === 'ap.release_payment',
).map((p) => p.key);

export const SOD_RULES: readonly SodRule[] = Object.freeze([
  { id: 'SOD-01', left: ['costing.edit'], right: ['costing.approve'] },
  { id: 'SOD-02', left: ['specs.edit'], right: ['specs.approve'] },
  { id: 'SOD-03', left: ['purchasing.create'], right: ['purchasing.approve'] },
  { id: 'SOD-04', left: ['ap.create'], right: ['ap.approve'] },
  { id: 'SOD-04', left: ['ap.approve'], right: ['ap.release_payment'] },
  { id: 'SOD-05', left: ['suppliers.edit_banking'], right: ['ap.release_payment'] },
  { id: 'SOD-06', left: ['gl.create'], right: ['gl.approve'] },
  { id: 'SOD-07', left: ['ar.apply_receipt'], right: ['ar.credit', 'ar.write_off'] },
  { id: 'SOD-08', left: ['inventory.adjust'], right: ['warehouse.apply'] },
  { id: 'SOD-09', left: ['roles.manage'], right: EMPOWERING },
  { id: 'SOD-10', left: ['cutting.create'], right: ['cutting.release'] },
  // CR-W2b-12 (CR-W2b-13 R-CORE-SOD-1, CR-W2b-7 R-PRG-2): whoever edits a programme's lines does not approve them.
  { id: 'SOD-11', left: ['programmes.edit'], right: ['programmes.approve'] },
] satisfies SodRule[]);

/** Pairs added after 02 §8.4 was written (CR log): SOD-11 programmes (CR-W2b-12). */
export const SOD_ADDITIONS: readonly string[] = Object.freeze(['SOD-11']);
export const SOD_IDS: readonly string[] = Object.freeze([...new Set(SOD_RULES.map((r) => r.id))]);

export interface SodConflict {
  readonly id: string;
  readonly left: PermissionKey;
  readonly right: PermissionKey;
}

/** All conflicting pairs in an effective permission set, minus pair ids covered by exceptions. */
export function sodConflicts(
  permissions: Iterable<PermissionKey>,
  exceptedIds: ReadonlySet<string> = new Set(),
): SodConflict[] {
  const held = new Set(permissions);
  const out: SodConflict[] = [];
  for (const rule of SOD_RULES) {
    if (exceptedIds.has(rule.id)) continue;
    for (const l of rule.left) {
      if (!held.has(l)) continue;
      for (const r of rule.right) if (held.has(r)) out.push({ id: rule.id, left: l, right: r });
    }
  }
  return out;
}
